// Per-city status arcs (docs/ART-DIRECTION.md §2.5: HP is among the brightest elements): while a city is damaged, a
// hairline HP arc in the owner's colour; while it is being taken (a fallen city captured, or neutral territory claimed —
// GDD §5), a capture arc inside it in the capturer's colour. The sim keeps captureProgress at 0 unless a hold is
// running. Shows the last-known state for fogged cities (MatchView.cityState). A city at full HP and not being
// captured draws nothing. Sized in CSS px and capped by local spacing like the rest of the city art (cityArt.ts); the
// tuner (ROADMAP D33) retunes it in place and can preview every city damaged or being captured.
// One draw call, on the emissive layer.

import { AdditiveBlending, DoubleSide, Group, InstancedBufferAttribute, InstancedMesh, Matrix4, PlaneGeometry, ShaderMaterial, Vector3 } from 'three'
import { InvariantViolation, type CityIndex, type MapCityView, type MatchSetupView, type PlayerId } from '@taxwar/net'
import type { CityLiveState } from '../../match/contracts'
import type { CityPreview } from '../../tuning/link'
import { DEG_TO_RAD, GLOBE_ALTITUDE, latLonToVector3 } from '../geo/globeSpace'
import { EMISSIVE_LAYER, assignLayer } from '../layers'
import { factionRole, type FactionRole, type Palette } from '../palette'
import { CITY_ART_AA_MARGIN_PX, cityArtKind, type CityArt } from './cityArt'
import { STATUS_FRAGMENT, STATUS_VERTEX } from './networkShaders'


const PREVIEW_HP = 0.6
const PREVIEW_CAPTURE = 0.4
/** Who takes a city in the 'capturing' preview: the other side (the viewer takes neutral territory). */
const PREVIEW_CAPTURER: Readonly<Record<FactionRole, FactionRole>> = { player: 'enemy', enemy: 'player', neutral: 'player' }

/** What one city's arcs show: HP and capture fractions, and whose colour the capture arc takes. */
interface CityArcs {
  hp: number
  capture: number
  capturer: FactionRole
}

/** Each preview rewrites a city's live arcs (given its owner's role) in place. */
const PREVIEW_ARCS: Readonly<Record<CityPreview, (arcs: CityArcs, owner: FactionRole) => void>> = {
  live: () => {},
  damaged: (arcs) => {
    arcs.hp = PREVIEW_HP
  },
  capturing: (arcs, owner) => {
    arcs.capture = PREVIEW_CAPTURE
    arcs.capturer = PREVIEW_CAPTURER[owner]
  },
}

/** Just above the rim rings. */
const STATUS_ALTITUDE = GLOBE_ALTITUDE.network + 0.0005
/** The status quad spans ±1 in its plane; the shader scales it to the arc radius plus the antialiasing margin. */
const QUAD_SIZE = 2
const COLOUR_COMPONENTS = 3

interface BuiltStatus {
  readonly viewer: PlayerId
  /** In CityIndex order. */
  readonly cities: readonly MapCityView[]
  readonly mesh: InstancedMesh
  readonly colours: InstancedBufferAttribute
  readonly captureColours: InstancedBufferAttribute
  readonly hp: InstancedBufferAttribute
  readonly capture: InstancedBufferAttribute
  readonly radii: InstancedBufferAttribute
  readonly caps: InstancedBufferAttribute
  readonly geometry: PlaneGeometry
  /** Last applied state per city, so colour-blind toggles and previews can repaint. */
  readonly states: (CityLiveState | null)[]
}

export class CityStatusLayer {
  readonly root = new Group()

  /** Render units per CSS px at the surface under the camera; set every frame. */
  private readonly worldPerPx = { value: 0 }
  private readonly intensity = { value: 0 }
  private readonly track = { value: 0 }
  private readonly hpStroke = { value: 0 }
  private readonly captureStroke = { value: 0 }
  private readonly gap = { value: 0 }
  private readonly material = new ShaderMaterial({
    uniforms: {
      uWorldPerPx: this.worldPerPx,
      uMarginPx: { value: CITY_ART_AA_MARGIN_PX },
      uIntensity: this.intensity,
      uTrack: this.track,
      uHpStroke: this.hpStroke,
      uCaptureStroke: this.captureStroke,
      uGap: this.gap,
    },
    vertexShader: STATUS_VERTEX,
    fragmentShader: STATUS_FRAGMENT,
    transparent: true,
    blending: AdditiveBlending,
    depthWrite: false,
    side: DoubleSide,
  })
  private status: BuiltStatus | null = null
  private palette: Palette
  private art: CityArt
  private preview: CityPreview = 'live'
  private readonly arcs: CityArcs = { hp: 1, capture: 0, capturer: 'neutral' }

  constructor(palette: Palette, art: CityArt) {
    this.palette = palette
    this.art = art
    this.applyArtUniforms(art)
  }

  /** `cityArtCaps` (cityArt.ts) caps each city's arcs by its local spacing. */
  setMap(view: MatchSetupView, cityArtCaps: Float32Array): void {
    this.clear()
    const count = view.cities.length
    const geometry = new PlaneGeometry(QUAD_SIZE, QUAD_SIZE)
    const radii = new InstancedBufferAttribute(new Float32Array(count), 1)
    const caps = new InstancedBufferAttribute(new Float32Array(count), 1)
    geometry.setAttribute('aRadiusPx', radii)
    geometry.setAttribute('aMaxWorldPerPx', caps)
    const hp = new InstancedBufferAttribute(new Float32Array(count).fill(1), 1)
    const capture = new InstancedBufferAttribute(new Float32Array(count), 1)
    const captureColours = new InstancedBufferAttribute(new Float32Array(count * COLOUR_COMPONENTS), COLOUR_COMPONENTS)
    geometry.setAttribute('aHp', hp)
    geometry.setAttribute('aCapture', capture)
    geometry.setAttribute('aCaptureColor', captureColours)
    const mesh = new InstancedMesh(geometry, this.material, count)
    const colours = new InstancedBufferAttribute(new Float32Array(count * COLOUR_COMPONENTS), COLOUR_COMPONENTS)
    mesh.instanceColor = colours

    // Quad plane (XY) onto the tangent plane with +X = east, +Y = north (unit axes; the shader sizes it in px), so arcs
    // fill clockwise from north seen from above.
    const up = new Vector3()
    const north = new Vector3()
    const east = new Vector3()
    const position = new Vector3()
    const matrix = new Matrix4()
    view.cities.forEach((city, i) => {
      if (city.index !== i) throw new InvariantViolation(`setup city ${city.name} has index ${city.index}, listed at ${i}`)
      const lat = city.lat * DEG_TO_RAD
      const lon = city.lon * DEG_TO_RAD
      latLonToVector3(city.lat, city.lon, 1, up)
      north.set(-Math.sin(lat) * Math.sin(lon), Math.cos(lat), -Math.sin(lat) * Math.cos(lon))
      east.set(Math.cos(lon), 0, -Math.sin(lon))
      position.copy(up).multiplyScalar(STATUS_ALTITUDE)
      matrix.makeBasis(east, north, up).setPosition(position)
      mesh.setMatrixAt(i, matrix)
    })
    assignLayer(mesh, EMISSIVE_LAYER)
    this.root.add(mesh)
    const status: BuiltStatus = {
      viewer: view.you,
      cities: view.cities,
      mesh,
      colours,
      captureColours,
      hp,
      capture,
      radii,
      caps,
      geometry,
      states: view.cities.map(() => null),
    }
    this.status = status
    this.writeCityArt(status, cityArtCaps)
  }

  /** Retunes the arcs (the tuner, ROADMAP D33): strokes and brightness now, `cityArtCaps` from the new sizing rule. */
  setCityArt(art: CityArt, cityArtCaps: Float32Array): void {
    this.art = art
    this.applyArtUniforms(art)
    if (this.status) this.writeCityArt(this.status, cityArtCaps)
  }

  /** Paints a city from its (possibly last-known) live state. */
  setCityState(index: CityIndex, state: CityLiveState): void {
    const status = this.requireStatus()
    if (index < 0 || index >= status.states.length) throw new InvariantViolation(`city index ${index} is not on the map`)
    status.states[index] = state
    this.paint(status, index, state)
    this.flagUploads(status)
  }

  /** Repaints every city's arcs from a new palette (a retune or a palette change). */
  setPalette(palette: Palette): void {
    this.palette = palette
    this.repaint()
  }

  /** The tuner's preview (CITY_PREVIEWS); players never leave 'live'. */
  setPreview(preview: CityPreview): void {
    if (this.preview === preview) return
    this.preview = preview
    this.repaint()
  }

  /** Once per frame, before rendering: `worldPerCssPixel` (the zoom) sizes the arcs. */
  update(worldPerCssPixel: number): void {
    this.worldPerPx.value = worldPerCssPixel
  }

  clear(): void {
    const status = this.status
    if (!status) return
    this.status = null
    this.root.clear()
    status.mesh.dispose()
    status.geometry.dispose()
  }

  dispose(): void {
    this.clear()
    this.root.removeFromParent()
    this.material.dispose()
  }

  private requireStatus(): BuiltStatus {
    if (!this.status) throw new InvariantViolation('city status layer has no map')
    return this.status
  }

  private applyArtUniforms(art: CityArt): void {
    this.intensity.value = art.brightness.hp
    this.track.value = art.brightness.track
    this.hpStroke.value = art.status.hpStroke
    this.captureStroke.value = art.status.captureStroke
    this.gap.value = art.status.gap
  }

  private writeCityArt(status: BuiltStatus, cityArtCaps: Float32Array): void {
    if (cityArtCaps.length !== status.cities.length) throw new InvariantViolation(`${cityArtCaps.length} city art caps for ${status.cities.length} cities`)
    for (const city of status.cities) {
      status.radii.setX(city.index, this.art.kinds[cityArtKind(city)].statusRadius)
      status.caps.setX(city.index, cityArtCaps[city.index] as number)
    }
    status.radii.needsUpdate = true
    status.caps.needsUpdate = true
  }

  private repaint(): void {
    const status = this.status
    if (!status) return
    status.states.forEach((state, index) => {
      if (state !== null) this.paint(status, index, state)
    })
    this.flagUploads(status)
  }

  private paint(status: BuiltStatus, index: number, state: CityLiveState): void {
    const ownerRole = factionRole(state.owner, status.viewer)
    const arcs = this.arcs
    arcs.hp = state.maxHp > 0 ? state.hp / state.maxHp : 0
    arcs.capture = state.captureProgress
    arcs.capturer = factionRole(state.captureBy, status.viewer)
    PREVIEW_ARCS[this.preview](arcs, ownerRole)
    const owner = this.palette[ownerRole]
    const capturer = this.palette[arcs.capturer]
    status.colours.setXYZ(index, owner.r, owner.g, owner.b)
    status.captureColours.setXYZ(index, capturer.r, capturer.g, capturer.b)
    status.hp.setX(index, arcs.hp)
    status.capture.setX(index, arcs.capture)
  }

  private flagUploads(status: BuiltStatus): void {
    status.colours.needsUpdate = true
    status.captureColours.needsUpdate = true
    status.hp.needsUpdate = true
    status.capture.needsUpdate = true
  }
}
